import React from 'react'
import PropTypes from 'prop-types'
import { Settings, Globe, TrendingUp, Calendar } from 'lucide-react'
import { cn } from '../utils/designSystem'

const AgentOptions = ({ agentType, options, onOptionsChange }) => {
  const updateOption = (key, value) => {
    console.log('🔍 AgentOptions - updateOption called:', { agentType, key, value })
    onOptionsChange({
      ...options,
      [key]: value
    })
  }

  if (agentType === 'gambar_agent') {
    const aspectRatios = [
      { value: '1:1', label: '1:1', description: 'Persegi' },
      { value: '16:9', label: '16:9', description: 'Lanskap lebar' },
      { value: '9:16', label: '9:16', description: 'Potret tinggi' },
      { value: '3:4', label: '3:4', description: 'Standar potret' },
      { value: '4:3', label: '4:3', description: 'Standar lanskap' }
    ]

    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Settings className="w-4 h-4 text-text-tertiary" />
          <span className="text-sm font-medium text-text-secondary">Aspect Ratio</span>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          {aspectRatios.map((ratio) => (
            <button
              key={ratio.value}
              type="button"
              onClick={() => updateOption('aspectRatio', ratio.value)}
              className={cn(
                "p-3 rounded-lg text-center transition-all duration-200 hover:scale-105",
                (options.aspectRatio || '1:1') === ratio.value
                  ? "bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg"
                  : "bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
              )}
            >
              <div className="font-semibold text-sm">{ratio.label}</div>
              <div className="text-xs opacity-80 mt-1">{ratio.description}</div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (agentType === 'berita_agent') {
    const languages = [
      { value: 'id', label: '🇮🇩 Indonesia', description: 'Bahasa Indonesia' },
      { value: 'en', label: '🇺🇸 English', description: 'English Language' }
    ]

    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Globe className="w-4 h-4 text-text-tertiary" />
          <span className="text-sm font-medium text-text-secondary">Language</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {languages.map((lang) => (
            <button
              key={lang.value}
              type="button"
              onClick={() => updateOption('language', lang.value)}
              className={cn(
                "p-3 rounded-lg text-left transition-all duration-200 hover:scale-105",
                (options.language || 'id') === lang.value
                  ? "bg-gradient-to-br from-red-500 to-orange-500 text-white shadow-lg"
                  : "bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
              )}
            >
              <div className="font-semibold text-sm">{lang.label}</div>
              <div className="text-xs opacity-80 mt-1">{lang.description}</div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (agentType === 'research_agent') {
    const languages = [
      { value: 'id', label: '🇮🇩 Indonesia', description: 'Bahasa Indonesia' },
      { value: 'en', label: '🇺🇸 English', description: 'English Language' }
    ]

    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Globe className="w-4 h-4 text-text-tertiary" />
          <span className="text-sm font-medium text-text-secondary">Language</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {languages.map((lang) => (
            <button
              key={lang.value}
              type="button"
              onClick={() => updateOption('language', lang.value)}
              className={cn(
                "p-3 rounded-lg text-left transition-all duration-200 hover:scale-105",
                (options.language || 'id') === lang.value
                  ? "bg-gradient-to-br from-purple-500 to-indigo-500 text-white shadow-lg"
                  : "bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
              )}
            >
              <div className="font-semibold text-sm">{lang.label}</div>
              <div className="text-xs opacity-80 mt-1">{lang.description}</div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (agentType === 'chart_agent') {
    const inputTypes = [
      { value: 'topic', label: '💡 Berdasarkan Topik', description: 'AI mencari data dari web' },
      { value: 'data', label: '📊 Data Manual', description: 'Input data sendiri' }
    ]

    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Settings className="w-4 h-4 text-text-tertiary" />
          <span className="text-sm font-medium text-text-secondary">Metode Input</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {inputTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => updateOption('inputType', type.value)}
              className={cn(
                "p-3 rounded-lg text-left transition-all duration-200 hover:scale-105",
                (options.inputType || 'topic') === type.value
                  ? "bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-lg"
                  : "bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
              )}
            >
              <div className="font-semibold text-sm">{type.label}</div>
              <div className="text-xs opacity-80 mt-1">{type.description}</div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (agentType === 'trending_agent') {
    const [showCountryDropdown, setShowCountryDropdown] = React.useState(false)
    
    const countries = [
      { value: 'Indonesia', label: '🇮🇩 Indonesia' },
      { value: 'United States', label: '🇺🇸 United States' },
      { value: 'China', label: '🇨🇳 China' },
      { value: 'Japan', label: '🇯🇵 Japan' },
      { value: 'Germany', label: '🇩🇪 Germany' },
      { value: 'India', label: '🇮🇳 India' },
      { value: 'United Kingdom', label: '🇬🇧 United Kingdom' },
      { value: 'France', label: '🇫🇷 France' },
      { value: 'Italy', label: '🇮🇹 Italy' },
      { value: 'Brazil', label: '🇧🇷 Brazil' },
      { value: 'Canada', label: '🇨🇦 Canada' },
      { value: 'Russia', label: '🇷🇺 Russia' },
      { value: 'South Korea', label: '🇰🇷 South Korea' },
      { value: 'Spain', label: '🇪🇸 Spain' },
      { value: 'Australia', label: '🇦🇺 Australia' },
      { value: 'Mexico', label: '🇲🇽 Mexico' },
      { value: 'Taiwan', label: '🇹🇼 Taiwan' },
      { value: 'Belgium', label: '🇧🇪 Belgium' },
      { value: 'Ireland', label: '🇮🇪 Ireland' },
      { value: 'Israel', label: '🇮🇱 Israel' },
      { value: 'Poland', label: '🇵🇱 Poland' },
      { value: 'Austria', label: '🇦🇹 Austria' },
      { value: 'Thailand', label: '🇹🇭 Thailand' },
      { value: 'Nigeria', label: '🇳🇬 Nigeria' },
      { value: 'Egypt', label: '🇪🇬 Egypt' },
      { value: 'South Africa', label: '🇿🇦 South Africa' },
      { value: 'Philippines', label: '🇵🇭 Philippines' },
      { value: 'Bangladesh', label: '🇧🇩 Bangladesh' },
      { value: 'Vietnam', label: '🇻🇳 Vietnam' },
      { value: 'Chile', label: '🇨🇱 Chile' },
      { value: 'Finland', label: '🇫🇮 Finland' },
      { value: 'Romania', label: '🇷🇴 Romania' },
      { value: 'Czech Republic', label: '🇨🇿 Czech Republic' },
      { value: 'Portugal', label: '🇵🇹 Portugal' },
      { value: 'New Zealand', label: '🇳🇿 New Zealand' },
      { value: 'Peru', label: '🇵🇪 Peru' },
      { value: 'Greece', label: '🇬🇷 Greece' },
      { value: 'Iraq', label: '🇮🇶 Iraq' },
      { value: 'Algeria', label: '🇩🇿 Algeria' },
      { value: 'Kazakhstan', label: '🇰🇿 Kazakhstan' },
      { value: 'Qatar', label: '🇶🇦 Qatar' },
      { value: 'Kuwait', label: '🇰🇼 Kuwait' },
      { value: 'Morocco', label: '🇲🇦 Morocco' },
      { value: 'Ecuador', label: '🇪🇨 Ecuador' },
      { value: 'Slovakia', label: '🇸🇰 Slovakia' },
      { value: 'Dominican Republic', label: '🇩🇴 Dominican Republic' },
      { value: 'Guatemala', label: '🇬🇹 Guatemala' },
      { value: 'Uruguay', label: '🇺🇾 Uruguay' },
      { value: 'Hungary', label: '🇭🇺 Hungary' },
      { value: 'Belarus', label: '🇧🇾 Belarus' }
    ]

    const today = new Date().toISOString().split('T')[0]
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const yesterdayStr = yesterday.toISOString().split('T')[0]
    const selectedCountry = countries.find(c => c.value === (options.region || 'Indonesia'))

    return (
      <div className="space-y-4">
        {/* Date Range */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm font-medium text-text-secondary">Rentang Tanggal</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-tertiary mb-1">Dari</label>
              <input
                type="date"
                value={options.fromDate || yesterdayStr}
                onChange={(e) => updateOption('fromDate', e.target.value)}
                className="w-full px-3 py-2 bg-bg-tertiary border border-border-primary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-xs text-text-tertiary mb-1">Sampai</label>
              <input
                type="date"
                value={options.toDate || today}
                onChange={(e) => updateOption('toDate', e.target.value)}
                className="w-full px-3 py-2 bg-bg-tertiary border border-border-primary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {/* Country Selection */}
        <div className="space-y-2 relative">
          <div className="flex items-center space-x-2">
            <Globe className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm font-medium text-text-secondary">Negara</span>
          </div>
          
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-primary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 flex items-center justify-between"
            >
              <span className="flex items-center space-x-2">
                <span>{selectedCountry?.label || '🇮🇩 Indonesia'}</span>
              </span>
              <svg className={cn('w-4 h-4 transition-transform', showCountryDropdown && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showCountryDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-bg-secondary border border-border-primary rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                {countries.map((country) => (
                  <button
                    key={country.value}
                    type="button"
                    onClick={() => {
                      updateOption('region', country.value)
                      setShowCountryDropdown(false)
                    }}
                    className={cn(
                      "w-full px-3 py-2 text-left hover:bg-bg-tertiary transition-colors flex items-center space-x-2",
                      (options.region || 'Indonesia') === country.value && "bg-orange-500/20 text-orange-400"
                    )}
                  >
                    <span>{country.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (agentType === 'video_agent') {
    const videoTypes = [
      { value: 'text_to_video', label: '📝 Text to Video', description: 'Generate video from text description' },
      { value: 'image_to_video', label: '🖼️ Image to Video', description: 'Generate video from image input' }
    ]

    const aspectRatios = [
      { value: '16:9', label: '16:9', description: 'Landscape' },
      { value: '9:16', label: '9:16', description: 'Portrait' },
      { value: '1:1', label: '1:1', description: 'Square' }
    ]

    return (
      <div className="space-y-4">
        {/* Video Type Selection */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium text-text-secondary">Video Type</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {videoTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => updateOption('videoType', type.value)}
                className={cn(
                  "p-3 rounded-lg text-left transition-all duration-200 hover:scale-105",
                  (options.videoType || 'text_to_video') === type.value
                    ? "bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg"
                    : "bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
                )}
              >
                <div className="font-semibold text-sm">{type.label}</div>
                <div className="text-xs opacity-80 mt-1">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Aspect Ratio Selection */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            <span className="text-sm font-medium text-text-secondary">Aspect Ratio</span>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            {aspectRatios.map((ratio) => (
              <button
                key={ratio.value}
                type="button"
                onClick={() => updateOption('aspectRatio', ratio.value)}
                className={cn(
                  "p-3 rounded-lg text-center transition-all duration-200 hover:scale-105",
                  (options.aspectRatio || '16:9') === ratio.value
                    ? "bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg"
                    : "bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
                )}
              >
                <div className="font-semibold text-sm">{ratio.label}</div>
                <div className="text-xs opacity-80 mt-1">{ratio.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Image Upload for Image to Video */}
        {(options.videoType === 'image_to_video') && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium text-text-secondary">Upload Image</span>
            </div>
            
            <div className="border-2 border-dashed border-border-primary rounded-lg p-4 text-center hover:border-violet-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    updateOption('imageFile', file)
                    // Create preview URL
                    const reader = new FileReader()
                    reader.onload = (e) => {
                      updateOption('imagePreview', e.target.result)
                    }
                    reader.readAsDataURL(file)
                  }
                }}
                className="hidden"
                id="video-image-upload"
              />
              <label htmlFor="video-image-upload" className="cursor-pointer">
                {options.imagePreview ? (
                  <div className="space-y-2">
                    <img 
                      src={options.imagePreview} 
                      alt="Preview" 
                      className="max-w-full h-32 object-cover rounded-lg mx-auto"
                    />
                    <p className="text-xs text-text-tertiary">Click to change image</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <svg className="w-8 h-8 text-text-tertiary mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <p className="text-sm text-text-secondary">Click to upload image</p>
                    <p className="text-xs text-text-tertiary">PNG, JPG, JPEG up to 10MB</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (agentType === 'research_agent') {
    const languages = [
      { value: 'en', label: '🇺🇸 English', description: 'English Language' },
      { value: 'id', label: '🇮🇩 Indonesia', description: 'Bahasa Indonesia' }
    ]

    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Globe className="w-4 h-4 text-text-tertiary" />
          <span className="text-sm font-medium text-text-secondary">Language</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {languages.map((lang) => (
            <button
              key={lang.value}
              type="button"
              onClick={() => updateOption('language', lang.value)}
              className={cn(
                "p-3 rounded-lg text-left transition-all duration-200 hover:scale-105",
                (options.language || 'en') === lang.value
                  ? "bg-gradient-to-br from-green-500 to-emerald-500 text-white shadow-lg"
                  : "bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
              )}
            >
              <div className="font-semibold text-sm">{lang.label}</div>
              <div className="text-xs opacity-80 mt-1">{lang.description}</div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (agentType === 'pdf_agent') {
    console.log('🔍 AgentOptions - PDF Agent rendered with options:', options)
    console.log('🔍 AgentOptions - pdfFile:', options?.pdfFile)
    console.log('🔍 AgentOptions - pdfPreview:', options?.pdfPreview)
    
    return (
      <div className="space-y-4">
        {/* PDF Upload */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm font-medium text-text-secondary">Upload PDF</span>
          </div>
          
          <div className="border-2 border-dashed border-border-primary rounded-lg p-6 text-center hover:border-orange-400 transition-colors">
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => {
                const file = e.target.files[0]
                if (file) {
                  // Validate file
                  if (file.type !== 'application/pdf') {
                    alert('Hanya file PDF yang diperbolehkan')
                    return
                  }
                  if (file.size > 50 * 1024 * 1024) { // 50MB
                    alert('Ukuran file terlalu besar. Maksimal 50MB')
                    return
                  }
                  
                  console.log('🔍 AgentOptions - Setting PDF file:', file)
                  
                  // Update both pdfFile and pdfPreview in one call
                  onOptionsChange({
                    ...options,
                    pdfFile: file,
                    pdfPreview: {
                      name: file.name,
                      size: file.size,
                      type: file.type
                    }
                  })
                }
              }}
              className="hidden"
              id="pdf-upload"
            />
            <label htmlFor="pdf-upload" className="cursor-pointer">
              {options.pdfPreview ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-3 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <svg className="w-8 h-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="text-left">
                      <p className="text-sm font-medium text-orange-400">{options.pdfPreview.name}</p>
                      <p className="text-xs text-orange-300">
                        {(options.pdfPreview.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-text-tertiary">Klik untuk mengganti file PDF</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <svg className="w-12 h-12 text-text-tertiary mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-text-secondary">Klik untuk upload file PDF</p>
                    <p className="text-xs text-text-tertiary mt-1">PDF hingga 50MB</p>
                  </div>
                </div>
              )}
            </label>
          </div>
          
          {options.pdfPreview && (
            <div className="bg-bg-tertiary/50 rounded-lg p-3">
              <p className="text-xs text-text-secondary mb-2">File siap untuk dianalisis:</p>
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs text-text-primary font-medium">{options.pdfPreview.name}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return null
}

AgentOptions.propTypes = {
  agentType: PropTypes.string.isRequired,
  options: PropTypes.object.isRequired,
  onOptionsChange: PropTypes.func.isRequired
}

export default AgentOptions